import { useState } from 'react';
import type { FormEvent } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { ApiRequestError } from '../api/client.js';
import { useSession } from '../auth/SessionProvider.js';
import { Alert, Button, Field, TextInput } from '../components/ui.js';
import { APP_MARK, APP_NAME } from '../lib/branding.js';

export function SignInPage() {
  const { signIn } = useSession();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await signIn(email.trim(), password);
      const from = (location.state as { from?: string } | null)?.from;
      navigate(from && from !== '/sign-in' ? from : '/', { replace: true });
    } catch (caught) {
      setError(
        caught instanceof ApiRequestError
          ? caught.message
          : 'Sign-in could not be completed. Please try again.',
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-slate-100 px-4 py-10">
      <div className="w-full max-w-md">
        <div className="mb-6 flex flex-col items-center gap-2 text-center">
          <span className="grid size-12 place-items-center rounded-lg bg-brand-700 text-lg font-bold text-white">
            {APP_MARK}
          </span>
          <h1 className="text-xl font-semibold text-slate-900">{APP_NAME}</h1>
          <p className="text-sm text-slate-600">
            Sign in to submit, review, and track repair requests.
          </p>
        </div>

        <form
          onSubmit={(event) => void handleSubmit(event)}
          className="flex flex-col gap-4 rounded-lg bg-white p-6 shadow-sm ring-1 ring-slate-200"
        >
          {error && <Alert variant="error">{error}</Alert>}

          <Field label="Email address" htmlFor="email" required>
            <TextInput
              id="email"
              type="email"
              name="email"
              autoComplete="username"
              required
              value={email}
              onChange={(event) => setEmail(event.target.value)}
            />
          </Field>

          <Field label="Password" htmlFor="password" required>
            <TextInput
              id="password"
              type="password"
              name="password"
              autoComplete="current-password"
              required
              value={password}
              onChange={(event) => setPassword(event.target.value)}
            />
          </Field>

          <Button type="submit" loading={submitting} className="mt-1 w-full">
            Sign in
          </Button>

          <Link
            className="text-center text-sm text-brand-700 hover:underline"
            to="/forgot-password"
          >
            Forgot your password?
          </Link>

          <p className="text-center text-xs text-slate-500">
            Accounts are created by an administrator. If you have not received an invitation,
            contact your program manager.
          </p>
        </form>

        <p className="mt-6 text-center text-xs text-slate-500">
          This is a private system for authorized users. Activity is logged.
        </p>
      </div>
    </div>
  );
}
